import React, { useEffect, useMemo, useRef, useState } from 'react';
import { editUser, showUser, showUserById } from '../../../services/admin/UserService.tsx';
import { deletePlayerAndRelatedData } from '../../../services/admin/UserDeleteService.tsx';
import { showUserbets } from '../../../services/admin/BetsService.tsx';
import CustomerTable from './CustomerTable.tsx';
import { Users } from '../../../models/User.tsx';
import { Timestamp } from 'firebase/firestore';
import { addTransaction } from '../../../services/admin/Transaction.tsx';
import { FaAngleDown, FaCheck, FaPen, FaTrash } from "react-icons/fa6";
import { Link, useLocation } from 'react-router-dom';
import { formatCurrency, formatNumbers, isFreePlayBet, tryParseFloat } from '../../../helpers/functions.tsx';
import { Modal } from 'bootstrap';
import { logUserActivity } from '../../../services/player/LoginService.tsx';
import { buildVisitedUserNavState, patchVisitedUserCaches } from '../../../helpers/adminVisitedUserCache.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import { FaSyncAlt } from 'react-icons/fa';
import {
    formatAdminFiguresDateTime,
    formatAdminFiguresShortDate,
} from '../../../helpers/adminFiguresTime.tsx';
import { agentCanAdjustBalances, isBlockedBalanceAction } from '../../../services/player/SportService.tsx';
import { useAgentPermissions } from '../../../helpers/useAgentPermissions.tsx';

const unlimitedOrNumber = (value?: number) =>
    (value ?? 0) > 0 ? formatNumbers(value) : "Unlimited";

const playerToFormData = (player: Partial<Users> | null | undefined, uppercasePin = false) => ({
    pin: uppercasePin ? (player?.pin?.toUpperCase() ?? '') : (player?.pin ?? ''),
    name: player?.name ?? '',
    wagerLimit: player?.wagerLimit ?? '',
    creditLimit: player?.creditLimit ?? '',
    status: player?.status ?? '',
    parlayMaxWager: unlimitedOrNumber(player?.parlayMaxWager),
    teaserMaxWager: unlimitedOrNumber(player?.teaserMaxWager),
    liveMaxWager: unlimitedOrNumber(player?.liveMaxWager),
    propsMaxWager: unlimitedOrNumber(player?.propsMaxWager),
    contestMaxWager: unlimitedOrNumber(player?.contestMaxWager),
    regularBetMaxWager: unlimitedOrNumber(player?.regularBetMaxWager),
});

const Customer = ({ customer, user, filters, onPlayerUpdate, onPlayerDeleted, isLoading }) => {

    const { user: currentUser, userIpData: userIpDataAdmin } = useUser();
    const { allowCreditDebitAdjustments } = useAgentPermissions(currentUser);
    const balanceActor = currentUser ? { ...currentUser, allowCreditDebitAdjustments } : currentUser;
    const [formData, setFormData] = useState(
        {
            pin: '', name: '', wagerLimit: '', creditLimit: '', status: '', parlayMaxWager: '', teaserMaxWager: '',
            liveMaxWager: '', propsMaxWager: '', contestMaxWager: '', regularBetMaxWager: ''
        });
    const [expandedPlayer, setExpandedPlayer] = useState(null);
    const [editing, setEditing] = useState({});
    const [selectedPlayer, setSelectedPlayer] = useState<Users | null>(null);
    const [playerToDelete, setPlayerToDelete] = useState<Users | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [formTransaction, setFormTransaction] = useState({
        createDate: formatAdminFiguresShortDate(),
        agent: '',
        customer: '',
        transactionType: 'deposit',
        description: 'Customer Deposit',
        amount: '',
        createBy: '',
    })
    const location = useLocation();
    const focusedUserName = location.state?.focusUser || '';
    const expandGenRef = useRef(0);
    const editingRef = useRef(editing);
    const lastFocusedRef = useRef('');
    editingRef.current = editing;

    const groupedPlayers = useMemo(() => {
        return customer.reduce((acc: any, player: any) => {
            if (!acc[player.parent]) {
                acc[player.parent] = [];
            }
            acc[player.parent].push(player);
            return acc;
        }, {});
    }, [customer]);

    const loadLivePlayerIntoForm = (playerId: string, snapshot: Users) => {
        const gen = ++expandGenRef.current;
        void (async () => {
            try {
                const live = snapshot.id
                    ? await showUserById(snapshot.id)
                    : await showUser(snapshot.userName);
                if (gen !== expandGenRef.current) return;
                if (editingRef.current[playerId]) return;

                setFormData(playerToFormData(live));
                onPlayerUpdate(playerId, {
                    pin: live.pin,
                    name: live.name,
                    creditLimit: live.creditLimit,
                    wagerLimit: live.wagerLimit,
                    status: live.status,
                    parlayMaxWager: live.parlayMaxWager,
                    teaserMaxWager: live.teaserMaxWager,
                    liveMaxWager: live.liveMaxWager,
                    propsMaxWager: live.propsMaxWager,
                    contestMaxWager: live.contestMaxWager,
                    regularBetMaxWager: live.regularBetMaxWager,
                });
            } catch (error) {
                console.error('Error loading live player password:', error);
            }
        })();
    };

    useEffect(() => {
        if (!focusedUserName || customer.length === 0) return;
        if (lastFocusedRef.current === focusedUserName) return;
        const newPlayer = customer.find((p: any) => p.userName === focusedUserName);
        if (newPlayer) {
            lastFocusedRef.current = focusedUserName;
            toggleExpandPlayer(newPlayer.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedUserName, customer]);


    const toggleExpandPlayer = (playerId: any) => {
        if (expandedPlayer === playerId) {
            expandGenRef.current += 1;
            setExpandedPlayer(null);
            setEditing({});
        } else {
            setExpandedPlayer(playerId);
            setEditing({});
            const selectedPlayer = customer.find((player: any) => player.id === playerId);
            if (selectedPlayer) {
                setFormData(playerToFormData(selectedPlayer));
                loadLivePlayerIntoForm(playerId, selectedPlayer);
            }
        }
    };

    const toggleEdit = (playerId: any) => {
        const isCurrentlyEditing = editing[playerId];

        if (isCurrentlyEditing) {
            saveChanges(playerId);
            return;
        }

        if (expandedPlayer !== playerId) {
            toggleExpandPlayer(playerId);
        }

        const selectedPlayer = customer.find((player: any) => player.id === playerId);
        if (selectedPlayer) {
            setFormData(playerToFormData(selectedPlayer, true));
        }

        setEditing(prev => ({
            ...prev,
            [playerId]: true
        }));
    };


    const handleFieldChange = (e: any, field: any) => {
        const value = e.target.value;
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    };

    const handleFieldChangeNumber = (e: any, field: any) => {
        const rawValue = e.target.value.replace(/,/g, '');

        if (field === 'creditLimit' && (rawValue === '' || rawValue === '-')) {
            setFormData(prev => ({
                ...prev,
                [field]: rawValue
            }));
            return;
        }

        if (rawValue === '' || !isNaN(Number(rawValue))) {
            setFormData(prev => ({
                ...prev,
                [field]: rawValue
            }));
        }
    };

    const saveChanges = async (playerId: any) => {
        try {
            const selectedPlayer = customer.find((player: any) => player.id === playerId);
            if (!selectedPlayer) return;

            const freshUser = await showUser(selectedPlayer.userName);
            const newCreditLimit = tryParseFloat(String(formData.creditLimit)) ?? 0;
            const freshBalance = freshUser?.balance ?? 0;
            const pendingBets = await showUserbets(selectedPlayer.userName);
            const freshPending = pendingBets.reduce(
                (sum, bet) => isFreePlayBet(bet) ? sum : sum + (bet.bet || 0),
                0,
            );
            const newAvailable = newCreditLimit + freshBalance - freshPending;

            const newData = {
                pin: formData.pin?.toUpperCase() ?? '',
                name: formData.name ?? selectedPlayer.name ?? "",
                wagerLimit: tryParseFloat(formData.wagerLimit) ?? 0,
                creditLimit: newCreditLimit,
                available: newAvailable,
                pending: freshPending,
                status: formData.status,
                parlayMaxWager: tryParseFloat(formData.parlayMaxWager) ?? 0,
                teaserMaxWager: tryParseFloat(formData.teaserMaxWager) ?? 0,
                liveMaxWager: tryParseFloat(formData.liveMaxWager) ?? 0,
                propsMaxWager: tryParseFloat(formData.propsMaxWager) ?? 0,
                contestMaxWager: tryParseFloat(formData.contestMaxWager) ?? 0,
                regularBetMaxWager: tryParseFloat(formData.regularBetMaxWager) ?? 0,
            };
            await editUser(playerId, newData);

            const changes: string[] = [];
            Object.keys(newData).forEach((key) => {
                const oldValue = (freshUser as any)?.[key] ?? selectedPlayer[key];
                const newValue = newData[key];
                if (oldValue !== newValue) {
                    changes.push(`${key}: ${oldValue ?? "N/A"} → ${newValue}`);
                }
            });

            if (currentUser?.userName && changes.length > 0) {
                await logUserActivity(currentUser.userName, `User Data Update: ${selectedPlayer.userName} | ${changes.join(", ")}`, userIpDataAdmin);
            }

            setEditing(prevEditing => ({
                ...prevEditing,
                [playerId]: false
            }));
            setFormData(playerToFormData({ ...selectedPlayer, ...newData }));
            onPlayerUpdate(playerId, newData);
            patchVisitedUserCaches(selectedPlayer.userName, newData);
            alert("Data saved correctly");

        } catch (error) {
            alert("Error saving data");
        }
    };

    const openDeletePlayer = (player: Users) => {
        setPlayerToDelete(player);
        setDeleteError(null);
    };

    const closeDeletePlayer = () => {
        if (isDeleting) return;
        setPlayerToDelete(null);
        setDeleteError(null);
    };

    const confirmDeletePlayer = async () => {
        if (!playerToDelete?.id) {
            setDeleteError('Player id is missing.');
            return;
        }

        setIsDeleting(true);
        setDeleteError(null);

        try {
            await deletePlayerAndRelatedData(playerToDelete);

            if (currentUser?.userName) {
                await logUserActivity(
                    currentUser.userName,
                    `Deleted player: ${playerToDelete.userName}`,
                    userIpDataAdmin
                );
            }

            if (expandedPlayer === playerToDelete.id) {
                setExpandedPlayer(null);
                setEditing({});
            }

            onPlayerDeleted?.(playerToDelete.id);
            setPlayerToDelete(null);
        } catch (error: any) {
            setDeleteError(error?.message || 'Error deleting player. Please try again.');
        } finally {
            setIsDeleting(false);
        }
    };

    const clearFormTransaction = () => {
        setFormTransaction({
            createDate: formatAdminFiguresShortDate(),
            agent: '',
            customer: '',
            transactionType: 'deposit',
            description: 'Customer Deposit',
            amount: '',
            createBy: '',
        });
    };
    const updateBalance = (subAgent: Users, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedPlayer(subAgent);
        setFormTransaction(prevData => ({
            ...prevData,
            customer: subAgent.userName,
            createBy: user?.userName ?? '',
            agent: user?.userName ?? '',
            description: 'Customer Deposit',
            userType: subAgent.userType

        }));
    };

    const calculateNewBalance = () => {
        if (!selectedPlayer) return 0;
        const amount = tryParseFloat(formTransaction.amount || '0');
        return formTransaction.transactionType === 'deposit'
            ? selectedPlayer.balance + amount
            : selectedPlayer.balance - amount;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        const sanitizedValue = name === 'amount' ? value.replace(/[^0-9.]/g, '') : value;

        setFormTransaction(prevData => {
            const updatedData = {
                ...prevData,
                [name]: sanitizedValue,
            };
            if (name === 'transactionType') {
                updatedData.description = value === 'Deposit' ? 'Customer Deposit' : 'Customer Withdrawal';
            }
            return updatedData;
        });
    };

    const saveTransaction = async () => {
        try {
            if (!selectedPlayer) {
                alert("No agent selected.");
                return;
            }
            if (isBlockedBalanceAction(balanceActor, formTransaction.transactionType)) {
                return;
            }
            if (formTransaction.amount.trim() === '' || isNaN(Number(formTransaction.amount))) {
                alert("Please enter a valid amount.");
                return;
            }
            const formDataWithTimestamp: any = {
                ...formTransaction,
                createDate: Timestamp.fromDate(new Date()),
            };

            formDataWithTimestamp.amount = tryParseFloat(formTransaction.amount);
            await addTransaction(formDataWithTimestamp);

            const updatedBalance =
                formTransaction.transactionType === 'deposit'
                    ? selectedPlayer.balance + formDataWithTimestamp.amount
                    : selectedPlayer.balance - formDataWithTimestamp.amount;

            const pendingBets = await showUserbets(selectedPlayer.userName);
            const cashPending = pendingBets.reduce(
                (sum, bet) => isFreePlayBet(bet) ? sum : sum + (bet.bet || 0),
                0,
            );
            const updatedAvailable =
                (selectedPlayer.creditLimit ?? 0) + updatedBalance - cashPending;

            if (!selectedPlayer?.id) {
                alert("Selected agent ID is missing.");
                return;
            }

            await editUser(selectedPlayer.id, {
                balance: updatedBalance,
                available: updatedAvailable,
                pending: cashPending,
            });
            setSelectedPlayer((prev) =>
                prev ? { ...prev, balance: updatedBalance, available: updatedAvailable, pending: cashPending } : null
            );

            if (currentUser?.userName) {
                await logUserActivity(currentUser.userName,
                    `Transaction: ${formTransaction.transactionType} | Amount: ${formDataWithTimestamp.amount} | Target User: ${selectedPlayer.userName}`, userIpDataAdmin
                );
            }

            alert("Transaction added successfully and balance updated.");
            clearFormTransaction();
            onPlayerUpdate(selectedPlayer.id, { balance: updatedBalance, available: updatedAvailable, pending: cashPending });

        } catch (error) {
            alert("Error adding transaction or updating balance.");
        }
    };


    const openBetDetailModal = () => {
        const modalElement = document.getElementById("balance");

        if (!modalElement) return;

        const existingInstance = Modal.getInstance(modalElement);

        if (existingInstance) {
            existingInstance.show();
        } else {
            const newModal = new Modal(modalElement);
            newModal.show();
        }
    };

    const goToConfirmModal = () => {
        if (isBlockedBalanceAction(balanceActor, formTransaction.transactionType)) {
            return;
        }
        const modal1El = document.getElementById("balance");
        const modal2El = document.getElementById("balanceConfirmCustomer");

        if (!modal1El || !modal2El) return;
        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);

        modal1El.style.display = "none";

        modal2.show();
    };

    const handleConfirm = () => {
        saveTransaction();

        const modal1El = document.getElementById("balance");
        const modal2El = document.getElementById("balanceConfirmCustomer");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";
    };

    const handleCancelConfirm = () => {
        const modal1El = document.getElementById("balance");
        const modal2El = document.getElementById("balanceConfirmCustomer");

        if (!modal1El || !modal2El) return;

        const modal2 = Modal.getInstance(modal2El) || new Modal(modal2El);
        const modal1 = Modal.getInstance(modal1El) || new Modal(modal1El);

        modal2.hide();
        modal1.hide();

        modal1El.style.display = "";

        clearFormTransaction();
    };

    return (
        <>
            {isLoading ? (
                <div className="loader-container">
                    <FaSyncAlt className="loader-icon-proyect" />
                </div>
            ) : (

                <table className="table header-table-customer table-custom">
                    <CustomerTable filters={filters} />
                    <tbody className="body-customer">
                        {Object.keys(groupedPlayers).map((parent) => (
                            <React.Fragment key={parent}>
                                <tr className="header-customer">
                                    <th colSpan={13} style={{ textAlign: 'left' }}>
                                        {parent}
                                    </th>
                                </tr>
                                {groupedPlayers[parent].map((player: any, index: any) => (

                                    <React.Fragment key={player.id}>

                                        <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-100'} no-padding-row`}>
                                            <td>
                                                <div className="color-td">
                                                    <Link
                                                        className='name'
                                                        to={`/user/${player.userName}`}
                                                        state={buildVisitedUserNavState(player)}
                                                        onClick={() => logUserActivity(currentUser!.userName, "View Weekly Figures", userIpDataAdmin)}
                                                    >
                                                        {player.userName}
                                                    </Link>
                                                    <FaAngleDown
                                                        className="icono-cursor ms-1"
                                                        onClick={() => toggleExpandPlayer(player.id)}
                                                    />
                                                </div>
                                            </td>
                                            {filters?.password && <td className='padding-left-customs'>{player.pin}</td>}
                                            {filters?.creditLimit && (
                                                <td className="centered-column">
                                                    ${formatNumbers(player.creditLimit)}
                                                </td>
                                            )}
                                            {filters?.wagerLimit && <td className="column-info">{player.regularBetMaxWager > 0 ? `$ ${formatNumbers(player.regularBetMaxWager)}` : "Unlimited"}</td>}
                                            {filters?.balanceForwardCarry && (
                                                <td className={`column-info ${player.balance > 0
                                                    ? "positive"
                                                    : player.balance < 0
                                                        ? "negative"
                                                        : ""
                                                    }`} onClick={(e) => {
                                                        if (!agentCanAdjustBalances(balanceActor)) return;
                                                        updateBalance(player, e);
                                                        // openBetDetailModal();
                                                    }}>
                                                    ${formatNumbers(player.balance)}
                                                </td>
                                            )}
                                            {filters?.pendingBalance && (
                                                <td className="centered-column">${formatNumbers(player.pending ?? 0)}</td>
                                            )}
                                            <td className="column-info">{player.status}</td>
                                            {filters?.email && <th className="centered-column">{player.email}</th>}
                                            {filters?.lastLogin && (
                                                <th className="centered-column">{formatAdminFiguresDateTime(player.lastLogin.toDate())}</th>
                                            )}
                                            <td className="centered-column actions-column">
                                                <div className="d-flex justify-content-center gap-1">
                                                    <button
                                                        className="btn btn-outline-primary body-customer px-2"
                                                        onClick={() => toggleEdit(player.id)}
                                                        title={editing[player.id] ? 'Save' : 'Edit'}
                                                        aria-label={`${editing[player.id] ? 'Save' : 'Edit'} ${player.userName}`}
                                                    >
                                                        {editing[player.id] ? <FaCheck /> : <FaPen />}
                                                    </button>
                                                    <button
                                                        className="btn btn-outline-danger body-customer px-2"
                                                        onClick={() => openDeletePlayer(player)}
                                                        disabled={isDeleting}
                                                        title="Delete"
                                                        aria-label={`Delete ${player.userName}`}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                        {expandedPlayer === player.id && (
                                            <tr>
                                                <td className='no-padding' colSpan={10} style={{ backgroundColor: '#0a2855d6' }}>
                                                    <div className="containerrR">
                                                        <div className="child">
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Password:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <input
                                                                        id={`player-pin-${player.id}`}
                                                                        name="pin"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        type="text"
                                                                        value={formData.pin}
                                                                        onChange={(e) => handleFieldChange(e, 'pin')}
                                                                        readOnly={!editing[player.id]}
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Name:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <input
                                                                        id="name"
                                                                        name="name"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        type="text"
                                                                        value={formData.name}
                                                                        onChange={(e) => handleFieldChange(e, 'name')}
                                                                        readOnly={!editing[player.id]}
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Credit Limit:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign`}>$</span>
                                                                    <input
                                                                        id="agent"
                                                                        name="agent"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={formData.creditLimit === '-' || formData.creditLimit === ''
                                                                            ? String(formData.creditLimit ?? '')
                                                                            : formatNumbers(formData.creditLimit)}
                                                                        type="text"
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'creditLimit')}
                                                                        readOnly={!editing[player.id]}
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Pending:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input id="pending" name="pending" type="text" autoComplete="off"
                                                                        className="input-customer"
                                                                        value={formatNumbers(player.pending ?? 0)}
                                                                        readOnly
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>

                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Balance:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign ${player.balance > 0 ? 'positive' : player.balance < 0 ? 'negative' : ''}`}>$</span>
                                                                    <input
                                                                        id="balance"
                                                                        name="balance"
                                                                        className={`input-customer ${player.balance > 0 ? 'positive' : player.balance < 0 ? 'negative' : ''}`}
                                                                        type="text"
                                                                        value={Number(player.balance).toLocaleString('en-US')}
                                                                        readOnly
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>

                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Available:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign ${player.available > 0 ? 'positive' : player.available < 0 ? 'negative' : ''}`}>$</span>
                                                                    <input
                                                                        id="available"
                                                                        name="available"
                                                                        className={`input-customer ${player.available > 0 ? 'positive' : player.available < 0 ? 'negative' : ''}`}
                                                                        type="text"
                                                                        value={Number(player.available).toLocaleString('en-US')}
                                                                        readOnly
                                                                        autoComplete="off"
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="child">
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Status:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <select
                                                                        id="status"
                                                                        name="status"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={formData.status}
                                                                        disabled={!editing[player.id]}
                                                                        onChange={(e) => handleFieldChange(e, 'status')}>
                                                                        <option value="Active">Active</option>
                                                                        <option value="Read-Only">Read Only</option>
                                                                        <option value="Disabled">Disabled</option>
                                                                    </select>
                                                                </div>
                                                            </div>
                                                            {/* <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Regular Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input
                                                                        id="regularBetMaxWager"
                                                                        name="regularBetMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={formData.regularBetMaxWager}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'regularBetMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Parlay Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input
                                                                        id="parlayMaxWager"
                                                                        name="parlayMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={(formData.parlayMaxWager)}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'parlayMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Teaser Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input
                                                                        id="teaserMaxWager"
                                                                        name="teaserMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={(formData.teaserMaxWager)}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'teaserMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="line-block2"></div> */}

                                                        </div>
                                                        {/* <div className="child">
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Live Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign`}>$</span>
                                                                    <input
                                                                        id="liveMaxWager"
                                                                        name="liveMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={(formData.liveMaxWager)}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'liveMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>




                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Props Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input
                                                                        id="propsMaxWager"
                                                                        name="propsMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={(formData.propsMaxWager)}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'propsMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>


                                                            <div className="line-block2"></div>
                                                            <div className="container-customer">
                                                                <div className="label-container">
                                                                    <span className="customer-selected">Contest Max Wager:</span>
                                                                </div>
                                                                <div className="input-container-customer">
                                                                    <span className={`dollar-sign `}>$</span>
                                                                    <input
                                                                        id="contestMaxWager"
                                                                        name="contestMaxWager"
                                                                        type="text"
                                                                        className={`input-customer ${editing[player.id] ? 'editable' : ''}`}
                                                                        value={(formData.contestMaxWager)}
                                                                        onChange={(e) => handleFieldChangeNumber(e, 'contestMaxWager')}
                                                                        readOnly={!editing[player.id]}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="line-block2"></div>
                                                        </div> */}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}

                            </React.Fragment>
                        ))}
                    </tbody>
                </table>

            )}
            <div className="modal fade" id="balance" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>New Transaction</span>
                            </h4>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <span className="form-label transactionManager">Transaction</span>
                                <select name="transactionType" className="selectManager" value={formTransaction.transactionType} onChange={handleInputChange}>
                                    {agentCanAdjustBalances(balanceActor) && (
                                        <>
                                            <option value="deposit">Deposits</option>
                                            <option value="withdrawal">Withdrawals</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <input type="text" id="transactionDate" className="dateTransaction" readOnly value={formTransaction.createDate} />
                            </div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Amount</span>
                                <input type="number" name="amount" className="numberTransaction" value={formTransaction.amount} autoComplete='off'
                                    onChange={handleInputChange} />
                            </div>
                            <div className="mb-3">
                                <span className="transactionManager2">Description</span>
                                <textarea name="description" className="descriptionManager" value={formTransaction.description}
                                    onChange={handleInputChange}
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" data-bs-dismiss="modal" onClick={clearFormTransaction}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={goToConfirmModal} disabled={!agentCanAdjustBalances(balanceActor)}>Continue</button>
                        </div>
                    </div>
                </div>
            </div>

            {playerToDelete && (
                <div
                    className="modal fade show"
                    tabIndex={-1}
                    role="dialog"
                    aria-modal="true"
                    style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                >
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {isDeleting ? 'Deleting Player...' : 'Delete Player'}
                                </h5>
                                {!isDeleting && (
                                    <button type="button" className="btn-close" onClick={closeDeletePlayer} />
                                )}
                            </div>
                            <div className="modal-body text-center">
                                {deleteError && (
                                    <div className="alert alert-danger text-start">
                                        <strong>Error:</strong> {deleteError}
                                    </div>
                                )}
                                {isDeleting && (
                                    <>
                                        <div className="spinner-border text-danger mb-3" />
                                        <p>Deleting player and related data, please wait...</p>
                                    </>
                                )}
                                {!isDeleting && !deleteError && (
                                    <p className="mb-0">
                                        This will permanently delete <strong>{playerToDelete.userName}</strong> and all
                                        related data: bets, transactions, activity, messages, and weekly figures.
                                        This cannot be undone.
                                    </p>
                                )}
                                {!isDeleting && deleteError && (
                                    <p className="mb-0 mt-2">
                                        You can try again or close this window.
                                    </p>
                                )}
                            </div>
                            <div className="modal-footer d-flex justify-content-between">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={closeDeletePlayer}
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={confirmDeletePlayer}
                                    disabled={isDeleting}
                                >
                                    Confirm Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="modal fade" id="balanceConfirmCustomer" tabIndex={-1}>
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title fs-5">
                                <span>Confirm Transaction</span>
                            </h4>
                            <button type="button" className="btn-close" onClick={handleCancelConfirm}></button>
                        </div>
                        <div className="modal-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Date</span>
                                <span className="transactionManager2">{formatAdminFiguresShortDate()}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Previous Balance</span>
                                <span className="transactionManager2">$ {formatCurrency(selectedPlayer?.balance)}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">Deposits</span>
                                <span className="transactionManager2">$ {formatCurrency(tryParseFloat(formTransaction.amount))}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="transactionManager2">New Balance</span>
                                <span className="transactionManager2">$ {formatCurrency(calculateNewBalance())}</span>
                            </div>
                        </div>
                        <div className="modal-footer d-flex justify-content-between">
                            <button type="button" className="btn btn-outline-danger" onClick={handleCancelConfirm}>Cancel</button>
                            <button type="button" className="btn btn-outline-success" onClick={handleConfirm}>Confirm</button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Customer;